import {useMemo} from 'react';

import {FunnelLogContextProviderProps} from '@udemy/funnel-tracking';
import {useI18n} from '@udemy/i18n';
import {mergeWithArrayOverwrite} from '@udemy/nextjs-core';
import {CourseSearchTriggerType, CourseSortOptionsType} from '@udemy/react-discovery-units';
import {CourseDirectoryContextProviderProps} from '@udemy/react-discovery-units/dist/@types/browse-course/course-directory/context/course-directory-provider.react-component';
import {useConsumerSubscriptionPlan} from '@udemy/subscription-graphql';
import {useUDData} from '@udemy/ud-data';

import {SEARCH_QUERY_OPTIONS_OVERRIDES} from '../constants';

const PAGE_TYPE = 'role-play-landing-page' as FunnelLogContextProviderProps['pageType'];

interface UseRolePlayCourseDirectoryResult extends CourseDirectoryContextProviderProps {
    hasOrganization: boolean;
}

export const useRolePlayCourseDirectory = () => {
    const {gettext} = useI18n();
    const personalPlanContext = useConsumerSubscriptionPlan();
    const udData = useUDData();

    const hasOrganization = Boolean(udData?.Config?.brand?.has_organization);

    const isPersonalPlanSubscriber =
        (personalPlanContext &&
            'isSubscriber' in personalPlanContext &&
            personalPlanContext.isSubscriber) ??
        false;

    const sortOptionsOverrides = useMemo(
        (): Partial<CourseSortOptionsType> => ({
            REVIEWS: {
                label: gettext('Most Reviewed'),
                key: 'most-reviewed',
                url: '',
            },
            RATING: {
                label: gettext('Highest Rated'),
                key: 'highest-rated',
                url: '',
            },
            TIME: {
                label: gettext('Newest'),
                key: 'newest',
                url: '',
            },
        }),
        [gettext],
    );

    const searchQueryOptionsOverrides = useMemo(() => {
        return mergeWithArrayOverwrite(SEARCH_QUERY_OPTIONS_OVERRIDES, {
            filters: {
                ...(isPersonalPlanSubscriber && {mustBeInConsumerSubscription: true}),
            },
        });
    }, [isPersonalPlanSubscriber]);

    return useMemo(
        (): UseRolePlayCourseDirectoryResult => ({
            hasOrganization,
            isPersonalPlanSubscriber,
            searchQueryOptionsOverrides,
            sortOptionsOverrides,
            pageType: PAGE_TYPE,
            courseSearchContext: {
                isDebug: undefined,
                originalQuery: undefined,
                refinedQuery: undefined,
                triggerType: CourseSearchTriggerType.BrowseRoleplay,
            },
        }),
        [
            hasOrganization,
            isPersonalPlanSubscriber,
            searchQueryOptionsOverrides,
            sortOptionsOverrides,
        ],
    );
};
